import { useTranslation } from 'react-i18next';
import { Chip, Modal, Select } from '@/components/ui';
import { useOrganizationOptions, useOrgLevelOptions } from './shared/useOrgLevelPicker';
import type { BulkAssignAccountsToOrgVM } from './useBulkAssignAccountsToOrg';

/**
 * EAA-1820 (frame 75, "Gán nhiều tài khoản vào đơn vị"): org picker, then that
 * org's Level picker, over every selected identity from the realm accounts
 * bulk bar — see `useBulkAssignAccountsToOrg` for why this is N sequential
 * creates rather than one bulk request.
 */
export function BulkAssignAccountsToOrgDialog({ vm }: { vm: BulkAssignAccountsToOrgVM }) {
    const { t } = useTranslation();
    const open = vm.targets.length > 0;
    const orgs = useOrganizationOptions(open);
    const levels = useOrgLevelOptions(vm.orgId, open);

    return (
        <Modal
            open={open}
            onCancel={vm.close}
            title={t('console.accounts.bulkAssignOrg.title', { count: vm.targets.length })}
            footer={
                <>
                    <button type="button" className="portal-btn" onClick={vm.close} disabled={vm.busy}>
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-primary"
                        onClick={vm.submit}
                        disabled={!vm.canSubmit}
                    >
                        {vm.busy
                            ? t('console.accounts.bulkAssignOrg.submitBusy')
                            : t('console.accounts.bulkAssignOrg.submitButton', { count: vm.targets.length })}
                    </button>
                </>
            }
        >
            <p className="portal-admin-hint">{t('console.accounts.bulkAssignOrg.hint')}</p>
            <div className="acc-bulk-assign-chips">
                {vm.targets.map((row) => (
                    <Chip key={row.sub}>{row.email || row.username}</Chip>
                ))}
            </div>
            {vm.alreadyPlacedCount > 0 && (
                <p className="portal-admin-hint">
                    {t('console.accounts.bulkAssignOrg.alreadyPlacedNote', { count: vm.alreadyPlacedCount })}
                </p>
            )}
            <div className="portal-admin-form">
                <label className="sa-dialog-field">
                    {t('console.accounts.bulkAssignOrg.orgLabel')}
                    <Select
                        value={vm.orgId || undefined}
                        onChange={(next) => vm.setOrgId((next as string) ?? '')}
                        options={orgs.options}
                        loading={orgs.loading}
                        placeholder={t('console.accounts.bulkAssignOrg.orgPlaceholder')}
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                    />
                </label>
                <label className="sa-dialog-field">
                    {t('console.accounts.bulkAssignOrg.levelLabel')}
                    <Select
                        value={vm.levelId || undefined}
                        onChange={(next) => vm.setLevelId((next as string) ?? '')}
                        options={levels.options}
                        loading={levels.loading}
                        disabled={!vm.orgId}
                        placeholder={
                            vm.orgId
                                ? t('console.accounts.bulkAssignOrg.levelPlaceholder')
                                : t('console.accounts.bulkAssignOrg.levelNeedsOrgPlaceholder')
                        }
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                    />
                </label>
                {vm.orgId && !levels.loading && levels.options.length === 0 && (
                    <p className="portal-admin-hint">{t('console.accounts.bulkAssignOrg.noLevelsHint')}</p>
                )}
                {vm.error && <p className="portal-admin-error">{vm.error}</p>}
            </div>
        </Modal>
    );
}

export default BulkAssignAccountsToOrgDialog;
